import PropTypes from "prop-types";
import { useMemo } from "react";

import styled from "styled-components/macro";

import {
  customProfileFieldHeaders,
  customProfileFieldIcons,
} from "ExtendedFields/ExtendedFieldsManager/AddFieldSelect/customFieldData";
import extendedFieldDataTransformers from "ExtendedFields/dataTransformers";
import extendedFieldHooks from "ExtendedFields/selectorHooks";
import FnrTypography from "FnrTypography/FnrTypography";

import FnrIcon from "UI/FnrIcon/FnrIcon";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap/FnrVerticalGap";

import { useFieldIsEditable } from "../hooks";

const { fieldTypes } = extendedFieldDataTransformers;

const HeaderContainer = styled("div")`
  ${({
    theme: {
      colors: { textSecondary },
    },
  }) => `
color: ${textSecondary};
font-size: 24px;
font-weight: 400;
`}
`;

const customCopy = {
  [fieldTypes.address]: (
    <>
      You can ask your members for an <em>address</em> with this field. You can
      choose the default country for address formatting and whether to ask for a
      country at all. Customize your field with a title, icon & permissions
      below.
    </>
  ),
  [fieldTypes.date]: (
    <>
      You can ask your members for any <em>date</em> with this field. These are
      great for baptism dates, birthdays, or anniversaries. Customize your field
      with a title, icon & permissions below.
    </>
  ),
  [fieldTypes.number]: (
    <>
      You can ask your members for a <em>number</em>. This is useful for asking
      something like a class year or number of children. They will only be
      allowed to enter numeric digits in this field. You can also specify a{" "}
      <strong>minimum</strong> and <strong>maximum</strong> number they are
      allowed to enter. Customize your field with a title, icon & permissions
      below.
    </>
  ),
  [fieldTypes.text]: (
    <>
      Your members can input anything they want in a <em>text box</em>. This is
      useful for asking something like a profession. If you&apos;re expecting
      longer responses you can have this display as a multi-line text box.
      Customize your field with a title, icon & permissions below.
    </>
  ),
  [fieldTypes.selectlist]: (
    <>
      With this field, you can give your members a pre-made selection to choose
      an answer from. You can also choose whether to allow your members to
      select one or multiple responses. Customize your field with a title, icon
      & permissions below.
    </>
  ),
  [fieldTypes.fileUpload]: (
    <>Use this field to request documents from your members.</>
  ),
};

const defaultCopy =
  "This field is used across all of Flocknote, so its title, icon, and customizations can't be changed. You can change who can view & edit this information in your network.";

const HeaderSwitch = () => {
  const [fieldType] = extendedFieldHooks.useSelectedFieldType();
  const [isCustom] = extendedFieldHooks.useSelectedIsCustom();
  const [currentTitle] = extendedFieldHooks.useTitle();
  const [currentIcon] = extendedFieldHooks.useIcon();
  const isEditable = useFieldIsEditable();
  const [isForGroup] = extendedFieldHooks.useIsForGroup();
  const [isNewField] = extendedFieldHooks.useIsNewField();
  const fieldIsEditable = useFieldIsEditable();

  const icon = useMemo(() => {
    if (!fieldIsEditable) return currentIcon;
    if (isCustom && isNewField) return customProfileFieldIcons[fieldType];
    return currentIcon || customProfileFieldIcons[fieldType];
  }, [currentIcon, fieldIsEditable, fieldType, isCustom, isNewField]);

  const title = useMemo(() => {
    if (!fieldIsEditable) return currentTitle;
    if (isCustom && isNewField) return customProfileFieldHeaders[fieldType];
    return currentTitle || customProfileFieldHeaders[fieldType];
  }, [currentTitle, fieldIsEditable, fieldType, isCustom, isNewField]);

  return (
    <FnrVerticalGap data-cy="ExtendedFields_HeaderSwitch">
      <HeaderContainer data-cy="ExtendedFields_HeaderSwitch_Header">
        <FnrHorizontalGap>
          <FnrIcon icon={icon} />
          <span>{title}</span>
        </FnrHorizontalGap>
      </HeaderContainer>
      {isCustom && isEditable && isNewField && (
        <div data-cy="Custom_Copy">
          <FnrTypography color="textSecondary">
            {customCopy[fieldType]}
          </FnrTypography>
        </div>
      )}
      {!isCustom && isForGroup && isNewField && (
        <div data-cy="Default_Copy">
          <FnrTypography color="textSecondary">{defaultCopy}</FnrTypography>
        </div>
      )}
    </FnrVerticalGap>
  );
};

HeaderSwitch.propTypes = {
  fieldType: PropTypes.oneOf(Object.values(fieldTypes)),
};

export default HeaderSwitch;
